import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { Search, Warning } from "@mui/icons-material";
import * as wjGrid from "@grapecity/wijmo.react.grid";
import { CellType } from "@grapecity/wijmo.grid";
import { Selector } from "@grapecity/wijmo.grid.selector";
import { FlexGridFilter } from "@grapecity/wijmo.react.grid.filter";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  getApartmentSearchLookups,
  searchAvailableApartments,
} from "apis/controllers/transfers";
import Dropdown from "qubCommon/Inputs/Dropdown";
import {
  buildApartmentSearchRequest,
  formatIncompatibilityReasons,
} from "./apartmentSearch";
import "./apartmentSelectionDialog.css";

const DEFAULT_FILTERS = {
  genderId: "",
  ageRangeId: "",
  isAmea: "",
};

const AMEA_OPTIONS = [
  { id: "true", name: "Ναι" },
  { id: "false", name: "Όχι" },
];

const formatChildrenCharacteristics = (characteristics) => {
  if (!Array.isArray(characteristics) || characteristics.length === 0) {
    return "-";
  }

  return characteristics
    .map((characteristic) =>
      `${characteristic.gender} (${characteristic.age})`
    )
    .join(", ");
};

const ApartmentSelectionDialog = ({
  open,
  transferId,
  apartments = [],
  onConfirm,
  onClose,
  childGender,
  childAge,
}) => {
  const gridRef = useRef(null);
  const selectorRef = useRef(null);
  const searchForm = useForm({ defaultValues: DEFAULT_FILTERS });
  const [searchLookups, setSearchLookups] = useState({
    gender: [],
    ageRange: [],
  });
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [overrideSearchEnabled, setOverrideSearchEnabled] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [gridVersion, setGridVersion] = useState(0);
  const [warningOpen, setWarningOpen] = useState(false);

  const resetSearchState = () => {
    setSearchResults([]);
    setSearchPerformed(false);
    setSearching(false);
    setSearchError("");
    searchForm.reset(DEFAULT_FILTERS);
  };

  const resetSelection = () => {
    setSelectedApartment(null);
    setWarningOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setOverrideSearchEnabled(false);
      setSelectedApartment(null);
      setWarningOpen(false);
      resetSearchState();
    }
    // The form instance is stable for the lifetime of this dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOverrideSearchChange = async (event) => {
    const enabled = event.target.checked;
    setOverrideSearchEnabled(enabled);
    resetSelection();
    resetSearchState();
    setGridVersion((version) => version + 1);

    if (
      !enabled ||
      (searchLookups.gender.length > 0 && searchLookups.ageRange.length > 0)
    ) {
      return;
    }

    setLoadingLookups(true);
    try {
      const response = await getApartmentSearchLookups();
      if (response?.status === "error") {
        throw new Error(response?.message || "Η φόρτωση των επιλογών απέτυχε.");
      }

      setSearchLookups({
        gender: response?.data?.gender || [],
        ageRange: response?.data?.ageRange || [],
      });
    } catch (error) {
      setSearchError(
        error?.message || "Προέκυψε σφάλμα κατά τη φόρτωση των επιλογών."
      );
    } finally {
      setLoadingLookups(false);
    }
  };

  const handleSearch = async (filters) => {
    resetSelection();
    setSearching(true);
    setSearchError("");
    setSearchPerformed(false);

    try {
      const response = await searchAvailableApartments(
        buildApartmentSearchRequest(transferId, filters)
      );

      if (response?.status === "error") {
        throw new Error(response?.message || "Η αναζήτηση απέτυχε.");
      }

      const results = Array.isArray(response?.data) ? response.data : [];
      setSearchResults(
        results.map((apartment) => ({
          ...apartment,
          characteristics: formatChildrenCharacteristics(
            apartment.childrenCharacteristics
          ),
        }))
      );
      setSearchPerformed(true);
      setGridVersion((version) => version + 1);
    } catch (error) {
      setSearchResults([]);
      setSearchPerformed(true);
      setSearchError(
        error?.message || "Προέκυψε σφάλμα κατά την αναζήτηση διαμερισμάτων."
      );
    } finally {
      setSearching(false);
    }
  };

  const visibleApartments = overrideSearchEnabled ? searchResults : apartments;
  const shouldShowGrid =
    !searching &&
    visibleApartments.length > 0 &&
    (!overrideSearchEnabled || searchPerformed);

  const requestApartmentConfirmation = (candidate) => {
    if (!candidate) return;

    if (candidate.isCompatibleWithChild === false) {
      setSelectedApartment(candidate);
      setWarningOpen(true);
      return;
    }

    onConfirm(candidate);
  };

  const confirmIncompatibleApartment = () => {
    if (!selectedApartment) return;

    setWarningOpen(false);
    onConfirm(selectedApartment);
  };

  return (
    <>
      <Dialog open={open} maxWidth="xl" fullWidth onClose={onClose}>
        <DialogTitle variant="h4">
          Επιλογή Διαμερίσματος ΜΗΔ για {childGender} - {childAge} ετών
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            height: 600,
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box display="flex" justifyContent="flex-end">
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={overrideSearchEnabled}
                  onChange={handleOverrideSearchChange}
                  color="warning"
                  sx={{
                    "& .MuiSwitch-track": {
                      backgroundColor: "#607d8b",
                      opacity: 0.7,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      opacity: 0.7,
                    },
                  }}
                />
              }
              label={
                overrideSearchEnabled
                  ? "Επιστροφή στα συμβατά διαμερίσματα"
                  : "Αναζήτηση σε όλα τα διαμερίσματα"
              }
            />
          </Box>

          {overrideSearchEnabled && (
            <FormProvider {...searchForm}>
              <Box
                component="form"
                onSubmit={searchForm.handleSubmit(handleSearch)}
                className="apartment-search-filters"
              >
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={3}>
                    <Dropdown
                      name="genderId"
                      options={searchLookups.gender}
                      label="Φύλο"
                      labelName="name"
                      valueName="id"
                      optionAll
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Dropdown
                      name="ageRangeId"
                      options={searchLookups.ageRange}
                      label="Ηλικιακή Ομάδα"
                      labelName="name"
                      valueName="id"
                      optionAll
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Dropdown
                      name="isAmea"
                      options={AMEA_OPTIONS}
                      label="ΑμεΑ"
                      labelName="name"
                      valueName="id"
                      optionAll
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Search />}
                      disabled={searching || loadingLookups || !transferId}
                      fullWidth
                    >
                      {loadingLookups ? "Φόρτωση..." : "Αναζήτηση"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </FormProvider>
          )}

          {searchError && <Alert severity="error">{searchError}</Alert>}

          {searching ? (
            <Box className="apartment-search-empty-state">
              <CircularProgress size={38} />
              <Typography color="text.secondary" mt={2}>
                Αναζήτηση διαθέσιμων διαμερισμάτων...
              </Typography>
            </Box>
          ) : shouldShowGrid ? (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <wjGrid.FlexGrid
                key={`${overrideSearchEnabled}-${gridVersion}`}
                frozenColumns={2}
                itemsSource={visibleApartments}
                isReadOnly={true}
                selectionMode="Row"
                headersVisibility="Column"
                className="apartment-selection-grid"
                maxHeight="95%"
                style={{ height: "95%" }}
                initialized={(grid) => {
                  gridRef.current = grid;
                  selectorRef.current = new Selector(grid, {
                    showCheckAll: false,
                  });

                  // CHECKBOX CLICK HANDLER
                  grid.hostElement.addEventListener("mousedown", (event) => {
                    const hitTest = grid.hitTest(event);
                    if (hitTest.cellType === 1) {
                      grid.beginUpdate();
                      grid.selectedRows.forEach(
                        (row) => (row.isSelected = false)
                      );
                      grid.endUpdate();
                      grid.rows[hitTest.row].isSelected = !event.target.checked;
                      gridRef.current.selectedApartment =
                        grid.rows[hitTest.row].dataItem;
                      if (!event.target.checked) {
                        setSelectedApartment(grid.rows[hitTest.row].dataItem);
                      } else {
                        setSelectedApartment(null);
                      }
                    }
                  });

                  // DOUBLE CLICK
                  grid.hostElement.addEventListener("dblclick", (event) => {
                    const hitTest = grid.hitTest(event);
                    if (hitTest.cellType === CellType.Cell) {
                      requestApartmentConfirmation(
                        grid.rows[hitTest.row].dataItem
                      );
                    }
                  });
                }}
              >
                <FlexGridFilter />
                <wjGrid.FlexGridColumn header="" width={50} align="center" />
                <wjGrid.FlexGridColumn
                  binding="name"
                  header="Όνομα"
                  width={460}
                >
                  <wjGrid.FlexGridCellTemplate
                    cellType="Cell"
                    template={({ item }) => (
                      <>
                        {!item.hasActiveUsers && (
                          <Tooltip title="Η ΜΗΔ δεν διαθέτει ενεργό προσωπικό!">
                            <Warning
                              color="warning"
                              fontSize="small"
                              sx={{ mr: 1 }}
                            />
                          </Tooltip>
                        )}
                        <span>{item.name}</span>
                      </>
                    )}
                  />
                </wjGrid.FlexGridColumn>
                <wjGrid.FlexGridColumn
                  binding="capacity"
                  header="Δυναμ."
                  width={80}
                />
                <wjGrid.FlexGridColumn
                  binding="available"
                  header="Διαθ."
                  width={80}
                  cssClass="qub-css-font-bold qub-css-bgcolor-yellow qub-css-forecolor-black"
                />
                <wjGrid.FlexGridColumn
                  binding="pendingRequests"
                  header="Αιτήσεις"
                  width={100}
                />
                <wjGrid.FlexGridColumn
                  binding="characteristics"
                  header="Ήδη διαμένοντες"
                  width={200}
                />
                <wjGrid.FlexGridColumn
                  binding="municipality"
                  header="Δήμος"
                  width={400}
                />
                <wjGrid.FlexGridColumn
                  binding="prefecture"
                  header="Περιφερειακή Ενότητα"
                  width={400}
                />
              </wjGrid.FlexGrid>
            </Box>
          ) : (
            <Box className="apartment-search-empty-state">
              <Typography variant="h5" fontWeight={700}>
                {overrideSearchEnabled && !searchPerformed
                  ? "Ορίστε τα φίλτρα αναζήτησης"
                  : "Δεν βρέθηκαν διαθέσιμα διαμερίσματα"}
              </Typography>
              <Typography color="text.secondary" mt={1}>
                {overrideSearchEnabled && !searchPerformed
                  ? "Επιλέξτε όσα χαρακτηριστικά επιθυμείτε και πατήστε Αναζήτηση."
                  : "Δοκιμάστε διαφορετικά φίλτρα αναζήτησης."}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Κλείσιμο</Button>
          <Button
            variant="contained"
            onClick={() => requestApartmentConfirmation(selectedApartment)}
            disabled={!selectedApartment}
          >
            Επιλογή
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={warningOpen}
        maxWidth="sm"
        fullWidth
        onClose={() => setWarningOpen(false)}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning color="warning" />
            <Typography variant="h4">Προειδοποίηση </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            Το επιλεγμένο διαμέρισμα δεν είναι συμβατό με τα χαρακτηριστικά του
            παιδιού.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {formatIncompatibilityReasons(
              selectedApartment?.incompatibilityReasons
            )}
          </Alert>
          <Typography sx={{ mt: 2 }}>
            Θέλετε να συνεχίσετε με την επιλογή αυτού του διαμερίσματος;
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningOpen(false)}>Ακύρωση</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmIncompatibleApartment}
          >
            Συνέχεια με την επιλογή
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApartmentSelectionDialog;
